import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Navigate } from 'react-router-dom';
import { getCachedAccessibleStores } from '@/lib/runtimeMetaCache';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Users,
  Store,
  Download,
  RefreshCw,
  Wallet,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { format, subDays, subMonths, startOfYear, endOfDay, startOfDay, startOfMonth, endOfMonth } from 'date-fns';
import { toast } from 'sonner';
import { formatStoreName } from '@/lib/storeUtils';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';

const OrdersDashboardPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [timePeriod, setTimePeriod] = useState('month'); // week, month, last_month, ytd, custom
  const [storeFilter, setStoreFilter] = useState('all');
  const [dataTier, setDataTier] = useState('hot');
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [customEndDate, setCustomEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [appliedCustomRange, setAppliedCustomRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });
  const [stores, setStores] = useState([]);

  // Define functions BEFORE early return and useEffect hooks
  const fetchStores = async () => {
    try {
      const data = await getCachedAccessibleStores();
      setStores(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch stores:', error);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { dateFrom, dateTo } = getDateRange();
      
      const params = new URLSearchParams({
        date_from: format(dateFrom, 'yyyy-MM-dd'),
        date_to: format(dateTo, 'yyyy-MM-dd'),
        store: storeFilter === 'all' ? '' : storeFilter,
        compare: compareEnabled ? 'true' : 'false',
        tier: dataTier,
      });

      const response = await fetch(
        `${getApiBaseUrl()}/api/orders/dashboard?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        toast.error('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const now = new Date();
    let dateFrom, dateTo;

    switch (timePeriod) {
      case 'week':
        dateFrom = startOfDay(subDays(now, 7));
        dateTo = endOfDay(now);
        break;
      case 'month':
        dateFrom = startOfMonth(now);
        dateTo = endOfDay(now);
        break;
      case 'last_month':
        dateFrom = startOfMonth(subMonths(now, 1));
        dateTo = endOfMonth(subMonths(now, 1));
        break;
      case 'ytd':
        dateFrom = startOfYear(now);
        dateTo = endOfDay(now);
        break;
      case 'custom':
        dateFrom = startOfDay(new Date(`${appliedCustomRange.start}T00:00:00`));
        dateTo = endOfDay(new Date(`${appliedCustomRange.end}T00:00:00`));
        break;
      default:
        dateFrom = startOfMonth(now);
        dateTo = endOfDay(now);
    }

    return { dateFrom, dateTo };
  };

  const handleApplyCustomRange = () => {
    if (!customStartDate || !customEndDate) {
      toast.error('Select both start and end date');
      return;
    }
    if (new Date(customStartDate) > new Date(customEndDate)) {
      toast.error('Start date cannot be after end date');
      return;
    }
    setAppliedCustomRange({ start: customStartDate, end: customEndDate });
    setTimePeriod('custom');
  };

  // Fetch stores for filter
  useEffect(() => {
    if (user && ['owner', 'admin', 'manager'].includes(user.role)) {
      fetchStores();
    }
  }, [user]);

  // Fetch dashboard data when filters change
  useEffect(() => {
    if (user && ['owner', 'admin', 'manager'].includes(user.role)) {
      fetchDashboardData();
    }
  }, [timePeriod, storeFilter, appliedCustomRange, compareEnabled, dataTier, user]);

  const revenueSummary = useMemo(() => {
    if (!dashboardData) {
      return {
        net: 0,
        gross: 0,
        netChange: 0,
        grossChange: 0,
      };
    }

    return {
      net: dashboardData.net_revenue || 0,
      gross: dashboardData.gross_revenue || 0,
      netChange: dashboardData.revenue_change || 0,
      grossChange: dashboardData.gross_revenue_change || 0,
    };
  }, [dashboardData]);

  const collectionsSummary = useMemo(() => {
    if (!dashboardData) {
      return {
        collected: 0,
        pending: 0,
        collectedChange: 0,
        pendingChange: 0,
      };
    }

    return {
      collected: dashboardData.collection_total || 0,
      pending: dashboardData.pending_balance_total || 0,
      collectedChange: dashboardData.collections_change || 0,
      pendingChange: dashboardData.pending_balance_change || 0,
    };
  }, [dashboardData]);

  // Stat cards data (excluding revenue summary card)
  const stats = useMemo(() => {
    if (!dashboardData) return [];
    
    return [
      {
        title: 'Total Orders',
        value: dashboardData.total_orders || 0,
        change: dashboardData.orders_change || 0,
        icon: ShoppingCart,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
      },
      {
        title: 'Average Order Value',
        value: `$${dashboardData.avg_order_value?.toFixed(2) || '0'}`,
        change: dashboardData.aov_change || 0,
        icon: TrendingUp,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
      },
      {
        title: 'New Customers',
        value: dashboardData.new_customers || 0,
        change: dashboardData.customers_change || 0,
        icon: Users,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
      },
    ];
  }, [dashboardData]);

  // Check if user has access (manager, admin, owner only) - AFTER all hooks
  if (!user || !['owner', 'admin', 'manager'].includes(user.role)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You don&apos;t have permission to view this dashboard. This page is only accessible to
              managers and above.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && !dashboardData) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const statusDenominator = Math.max(
    (dashboardData?.by_status || []).reduce((sum, item) => sum + (item.count || 0), 0),
    1
  );

  const byStoreChartData = (dashboardData?.by_store || []).map((item) => ({
    ...item,
    store_display: formatStoreName(item.store),
  }));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Orders Dashboard</h1>
          <p className="text-muted-foreground">Revenue and order analytics</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {/* Store Filter */}
          <Select value={storeFilter} onValueChange={setStoreFilter}>
            <SelectTrigger className="w-[140px]">
              <Store className="mr-2 h-4 w-4" />
              <SelectValue placeholder="All Stores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores</SelectItem>
              {stores.map((store) => (
                <SelectItem key={store.store_id} value={store.store_id}>
                  {formatStoreName(store.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Time Period Filter */}
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Time Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>

          <Select value={dataTier} onValueChange={setDataTier}>
            <SelectTrigger className="w-[150px]" data-testid="orders-dashboard-tier-filter">
              <SelectValue placeholder="Data Tier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hot">Hot Data</SelectItem>
              <SelectItem value="cold">Cold Data</SelectItem>
              <SelectItem value="all">Hot + Cold</SelectItem>
            </SelectContent>
          </Select>

          {/* Compare Toggle */}
          <Button
            variant={compareEnabled ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCompareEnabled(!compareEnabled)}
          >
            Compare
          </Button>

          {/* Refresh */}
          <Button variant="outline" size="icon" onClick={fetchDashboardData}>
            <RefreshCw className="h-4 w-4" />
          </Button>

          {/* Export */}
          <Button variant="outline" size="icon">
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground" data-testid="orders-dashboard-revenue-summary-title">Revenue</p>
                <div>
                  <p className="text-xs text-muted-foreground">Net Rev (ex GST)</p>
                  <p className="text-xl font-bold" data-testid="orders-dashboard-net-revenue-value">${revenueSummary.net.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Gross Rev (incl GST)</p>
                  <p className="text-lg font-semibold" data-testid="orders-dashboard-gross-revenue-value">${revenueSummary.gross.toLocaleString()}</p>
                </div>
                {compareEnabled && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Badge variant={revenueSummary.netChange > 0 ? 'success' : 'destructive'}>
                      Net {revenueSummary.netChange > 0 ? '+' : ''}{revenueSummary.netChange.toFixed(1)}%
                    </Badge>
                    <Badge variant={revenueSummary.grossChange > 0 ? 'success' : 'destructive'}>
                      Gross {revenueSummary.grossChange > 0 ? '+' : ''}{revenueSummary.grossChange.toFixed(1)}%
                    </Badge>
                  </div>
                )}
              </div>
              <div className="p-3 rounded-full bg-green-50">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground" data-testid="orders-dashboard-collections-summary-title">Collections</p>
                <div>
                  <p className="text-xs text-muted-foreground">Collected</p>
                  <p className="text-xl font-bold" data-testid="orders-dashboard-collected-value">${collectionsSummary.collected.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pending Balance</p>
                  <p className="text-lg font-semibold" data-testid="orders-dashboard-pending-balance-value">${collectionsSummary.pending.toLocaleString()}</p>
                </div>
                {compareEnabled && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Badge variant={collectionsSummary.collectedChange > 0 ? 'success' : 'destructive'}>
                      Collected {collectionsSummary.collectedChange > 0 ? '+' : ''}{collectionsSummary.collectedChange.toFixed(1)}%
                    </Badge>
                    <Badge variant={collectionsSummary.pendingChange > 0 ? 'destructive' : 'success'}>
                      Pending {collectionsSummary.pendingChange > 0 ? '+' : ''}{collectionsSummary.pendingChange.toFixed(1)}%
                    </Badge>
                  </div>
                )}
              </div>
              <div className="p-3 rounded-full bg-amber-50">
                <Wallet className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {stats.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <p className="text-xl font-bold mt-1">{stat.value}</p>
                  {compareEnabled && stat.change !== 0 && (
                    <Badge
                      variant={stat.change > 0 ? 'success' : 'destructive'}
                      className="mt-2"
                    >
                      {stat.change > 0 ? '+' : ''}
                      {stat.change.toFixed(1)}%
                    </Badge>
                  )}
                </div>
                <div className={`p-3 rounded-full ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Chart */}
      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <CardTitle>Revenue (Net vs Gross) & Orders Trend</CardTitle>
            <div className="flex flex-wrap items-center gap-2" data-testid="orders-dashboard-chart-date-controls">
              <div className="flex items-center gap-1">
                <label className="text-xs text-muted-foreground" htmlFor="orders-dashboard-start-date">Start</label>
                <Input
                  id="orders-dashboard-start-date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="h-8 w-[150px]"
                  data-testid="orders-dashboard-start-date-input"
                />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs text-muted-foreground" htmlFor="orders-dashboard-end-date">End</label>
                <Input
                  id="orders-dashboard-end-date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="h-8 w-[150px]"
                  data-testid="orders-dashboard-end-date-input"
                />
              </div>
              <Button
                variant={timePeriod === 'custom' ? 'default' : 'outline'}
                size="sm"
                onClick={handleApplyCustomRange}
                data-testid="orders-dashboard-apply-custom-range-button"
              >
                Apply Custom Range
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={520}>
            <AreaChart data={dashboardData?.chart_data || []}>
              <defs>
                <linearGradient id="colorGrossRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                stroke="#888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                yAxisId="left"
                stroke="#888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `$${value}`}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
                formatter={(value, name) => {
                  const numericValue = typeof value === 'number' ? value : Number(value || 0);
                  const labelMap = {
                    gross_revenue: 'Gross Revenue (incl GST)',
                    'Gross Revenue (incl GST)': 'Gross Revenue (incl GST)',
                    net_revenue: 'Net Revenue (ex GST)',
                    'Net Revenue (ex GST)': 'Net Revenue (ex GST)',
                    revenue: 'Net Revenue (ex GST)',
                    orders: 'No. of Orders',
                    Orders: 'No. of Orders',
                    'No. of Orders': 'No. of Orders',
                    prev_revenue: 'Prev Net Revenue',
                    'Prev Revenue': 'Prev Net Revenue',
                    prev_orders: 'Prev No. of Orders',
                    'Prev Orders': 'Prev No. of Orders',
                  };
                  const orderSeriesNames = new Set(['orders', 'Orders', 'No. of Orders', 'prev_orders', 'Prev Orders']);
                  const resolvedLabel = labelMap[name] || String(name);
                  const resolvedValue = orderSeriesNames.has(String(name))
                    ? numericValue
                    : `$${numericValue.toLocaleString()}`;

                  return [resolvedValue, resolvedLabel];
                }}
              />
              <Legend />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="gross_revenue"
                stroke="#7c3aed"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorGrossRevenue)"
                name="Gross Revenue (incl GST)"
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="net_revenue"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ fill: '#10b981', r: 3 }}
                name="Net Revenue (ex GST)"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="orders"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6', r: 4 }}
                name="No. of Orders"
              />
              {compareEnabled && dashboardData?.comparison_data && (
                <>
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="prev_revenue"
                    stroke="#10b981"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name="Prev Revenue"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="prev_orders"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name="Prev Orders"
                  />
                </>
              )}
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Bottom Row: Store Performance & Top Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales by Store */}
        <Card>
          <CardHeader>
            <CardTitle>Sales by Store</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={byStoreChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" stroke="#888" fontSize={12} />
                <YAxis dataKey="store_display" type="category" stroke="#888" fontSize={12} width={100} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                  formatter={(value) => `$${value.toLocaleString()}`}
                />
                <Bar dataKey="revenue" fill="#10b981" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Order Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Order Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboardData?.by_status?.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <Badge variant="outline">{item.status}</Badge>
                    <div className="flex-1">
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500"
                          style={{
                            width: `${
                              (item.count / statusDenominator) * 100
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <span className="text-sm font-medium ml-4">{item.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrdersDashboardPage;
